import { AppTableControls } from 'common/TableControls/TableControls';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppSelect } from 'shared/UI/UIKit/Select';
import { AppInputNumber } from 'shared/UI/UIKit/InputNumber';
import { educationParallelTextRepresentation } from 'entities/education-class';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppText } from 'shared/UI/UIKit/Text';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import { getTextResolver } from 'shared/UI/UIKit/Select/utils/resolvers/get-text-resolver';
import type { EducationClass } from 'services/api';
import type { CurriculumElement } from 'services/api/educationPlans';
import type { Subject } from 'services/api/subjects';
import type { AppTableColumn, AppTableSingleColumn } from 'shared/UI/UIKit/Table';
import styles from '../ui/styles.module.scss';
import { getClassKey } from './utils';

export type EducationPlanRow = {
  key: string;
  subjectId: number | null;
  subjectName: string | null;
  curriculumElements: CurriculumElement[];
  [classId: string]: number | string | CurriculumElement[] | null;
};

export const ADD_SUBJECT_ROW_KEY = 'education-plan-add-subject-row';
export const TOTAL_ROW_KEY = 'education-plan-total-row';

type GetColumnsProps = {
  classes: EducationClass[];
  subjects: Subject[];
  selectedSubjectIds: number[];
  onSubjectChange: (rowKey: string, subjectId: number) => void;
  onDeleteRow: (rowKey: string) => void;
  onAddSubjectRow: () => void;
  onAddClassClick: () => void;
  onHoursChange: (rowKey: string, classId: number, hours: number | null) => void;
  onRemoveClass: (classKey: string) => void;
  editMode: boolean;
};

export const getColumns = ({
  classes,
  subjects,
  selectedSubjectIds,
  onSubjectChange,
  onDeleteRow,
  onAddSubjectRow,
  onAddClassClick,
  onHoursChange,
  onRemoveClass,
  editMode,
}: GetColumnsProps): AppTableColumn<EducationPlanRow>[] => {
  const baseColumns: AppTableColumn<EducationPlanRow>[] = [
    {
      key: 'subjectName',
      title: 'Название предмета',
      dataIndex: ['subjectId'],
      fixed: 'left',
      minWidth: 260,
      render: (value: number | null, record: EducationPlanRow) => {
        if (record.key === ADD_SUBJECT_ROW_KEY) {
          if (!editMode) {
            return null;
          }
          return (
            <AppButton type="outline" htmlType="button" onClick={onAddSubjectRow}>
              Добавить предмет
            </AppButton>
          );
        }
        if (record.key === TOTAL_ROW_KEY) {
          return (
            <AppText size={18} style={{ paddingLeft: 8 }}>
              Всего часов
            </AppText>
          );
        }
        if (!editMode) {
          return (
            <AppText size={16} style={{ fontWeight: 400 }}>
              {record.subjectName}
            </AppText>
          );
        }
        return (
          <AppSelect
            value={value ?? undefined}
            showSearch
            dropdownMatchSelectWidth={false}
            filterOption={getTextResolver('name')}
            options={subjects.map((subject) => ({
              value: subject.id,
              name: formatSubjectWithLevel(subject.name, subject.educationLevel),
              label: (
                <AppFlex direction="column" unit="2" alignItems="flex-start">
                  <span>{formatSubjectWithLevel(subject.name, subject.educationLevel)}</span>
                  {(subject.id === 999 || subject.id === 998) && (
                    <span style={{ color: 'gray', fontSize: 12 }}>
                      Будет добавлено при генерации расписания
                    </span>
                  )}
                </AppFlex>
              ),
              disabled:
                (selectedSubjectIds.includes(subject.id) && subject.id !== value) ||
                subject.id === 999 ||
                subject.id === 998,
            }))}
            disabled={!editMode}
            onChange={(subjectId) => onSubjectChange(record.key, subjectId as number)}
          />
        );
      },
    },
  ];

  const classColumns: AppTableSingleColumn<EducationPlanRow>[] = classes.map((cls) => {
    const classKey = String(cls.id);

    return {
      key: `class-${cls.id}`,
      title: (
        <AppFlex direction="column" alignItems="center" justifyContent="center" unit="4">
          <span>{`${educationParallelTextRepresentation[cls.parallel]}${cls.litera}`}</span>
          {editMode && <AppTableControls remove={() => onRemoveClass(getClassKey(cls))} />}
        </AppFlex>
      ),
      dataIndex: [classKey],
      width: 120,
      align: 'center',
      className: styles.cell,
      render: (value: number | null, record: EducationPlanRow) => {
        if (!editMode || record.key === ADD_SUBJECT_ROW_KEY || record.key === TOTAL_ROW_KEY) {
          return <span className={styles.hoursValue}>{value ?? null}</span>;
        }

        return (
          <AppInputNumber
            defaultValue={value ?? undefined}
            min={0}
            size="small"
            onWheel={(e) => e.currentTarget.blur()}
            className={styles.hoursInput}
            onBlur={(e) => {
              const newValue = e.target.value === '' ? null : Number(e.target.value);
              const normalized =
                newValue === null || Number.isNaN(newValue) || newValue < 0 ? null : newValue;
              if (normalized !== value) {
                onHoursChange(record.key, cls.id, normalized);
              }
            }}
          />
        );
      },
    };
  });

  const addClassColumn: AppTableSingleColumn<EducationPlanRow> = {
    key: 'add-class',
    title: editMode ? (
      <AppButton type="outline" htmlType="button" onClick={onAddClassClick}>
        Выбрать классы
      </AppButton>
    ) : (
      ''
    ),
    dataIndex: [],
    minWidth: 140,
    align: 'center',
    fixed: 'right',
    render: (_: unknown, record: EducationPlanRow) => {
      if (record.key === ADD_SUBJECT_ROW_KEY || record.key === TOTAL_ROW_KEY || !editMode) {
        return null;
      }
      return <AppTableControls remove={() => onDeleteRow(record.key)} />;
    },
  };

  return [...baseColumns, ...classColumns, addClassColumn];
};
